<script>
import storageService from "@/services/storage.service";
import { setApplicationSubmitted } from "@/services/applicantProgress";
import Layout from "../../layouts/auth";
import appConfig from "@/app.config";
import axios from "axios";
import { required, email, helpers, maxLength } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

import { PublicClientApplication } from "@azure/msal-browser";

// Stricter than the built-in `email` rule: needs name@domain.tld (TLD of 2+ letters),
// no spaces, and no second "@".
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

// Forgot Password component
export default {
  setup() {
    return { v$: useVuelidate() };
  },
  mounted() {},
  validations: {
    email: {
      required: helpers.withMessage("Email is required", required),
      email: helpers.withMessage("Please enter valid email", email),
      format: helpers.withMessage(
        "Enter a valid email address, e.g. name@example.com",
        helpers.regex(EMAIL_FORMAT),
      ),
    },
    maxlength: {
      required: helpers.withMessage("Maxlength is required", required),
      maxLength: maxLength(4),
    },
  },
  page: {
    title: "Forgot Password",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
  },
  async created() {
    this.$msalInstance = new PublicClientApplication(
      this.$store.state.msalConfig,
    );
    this.$emitter.on(
      "logout",
      async function () {
        await this.SignOut();
      }.bind(this),
    );
  },
  data() {
    return {
      email: "",
      showForm1: true, // Initially show the first form
      showForm2: false,
      copiedEmail: "",
      isLoading: false,
      isSubmitting: false,
      isDisabled: false,
      otp: "",
      toload: true,
      account: undefined,
      containers: [],
      myat: "",
      make_it_load: false,
      showErrorMessage: false,
      myerrors: "",
      signin: "https://microsoft.com",
      // referenced by the template; defined here so Vue does not warn
      submitted: false,
      isResetError: false,
      error: "",
    };
  },
  methods: {
    async tryToReset(override = false) {
  
      this.v$.email.$touch();
      if (this.v$.email.$invalid) return;
      this.isLoading = true;
      this.isDisabled = true;
      try {
        const response = await axios.post("/accounts/otp_send/", {
          email: this.email,
          overide: override === true,
          page: "customer",
        });
        if (response.status === 200 && !response.data.error) {
          this.copiedEmail = this.email;
          this.showForm1 = false;
          this.showForm2 = true;
          return;
        }
        this.myerrors =
          response.data.msg ||
          response.data.error ||
          "Unable to send OTP. Please try again.";
        this.showErrorMessage = true;
      } catch (error) {
        const statusCode = error.response?.status;
        const responseData = error.response?.data;

        if (statusCode === 401) {
          // Account deactivated
          this.myerrors =
            responseData?.msg ||
            "Your account is deactivated. Please contact Administrator.";
        } else if (statusCode === 400) {
          // Validation error
          this.myerrors =
            responseData?.msg ||
            responseData?.error ||
            "Invalid email address.";
        } else if (statusCode === 429) {
          this.myerrors =
            responseData?.detail ||
            "Too many OTP requests. Please try again later.";
        } else {
          this.myerrors =
            responseData?.msg ||
            responseData?.error ||
            "Network or server error sending OTP. Please try again.";
        }
        this.showErrorMessage = true;
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
        setTimeout(() => {
          this.showErrorMessage = false;
        }, 3000);
      }
    },
    // Go back from the OTP step to the email step so a wrong email can be fixed.
    changeEmail() {
      this.showForm2 = false;
      this.showForm1 = true;
      this.isLoading = false;
      this.isDisabled = false;
      this.isSubmitting = false;
      this.showErrorMessage = false;
      this.otp = "";
      this.copiedEmail = "";
      // `email` keeps its value so the user only edits the typo
      this.v$.email.$reset();
    },
    async tryToLogin() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      let sr = this.copiedEmail;
      axios
        .post("/accounts/login/", { email: sr, otp: this.otp })
        .then((response) => {
          this.isSubmitting = false;
          if (response.data.status == "SUCCESS") {
            const accessToken = response.data.access;
            const role = response.data.role;

            if (role === "staff" || role === "verification officer") {
                  storageService.clear();
                  this.myerrors = "This is not an applicant account. Please use the staff login.";
                  this.showErrorMessage = true;
                  setTimeout(() => { this.showErrorMessage = false; }, 3500);
                  return;
                }

            storageService.setItem("accessToken", accessToken);
            storageService.setItem("userEmail", sr);

            if (role === "applicant") {
              storageService.setItem("userType", "applicant");
              setApplicationSubmitted(response.data.submit === "Yes");
              if (response.data.submit === "Yes") {
                this.$router.push({ name: "default" });
              } else {
                this.$router.push({ name: "membership_application" });
              }
            }

          } else {
            this.myerrors = response.data.error || response.data.message || "Invalid credentials";
            this.showErrorMessage = true;
            setTimeout(() => {
              this.showErrorMessage = false;
            }, 2500);
            storageService.clear();
          }
        })
        .catch((error) => {
          this.isSubmitting = false;
          if (error.response && error.response.status === 401) {
            storageService.clear();
          }
          this.myerrors =
            (error.response &&
              error.response.status === 401 &&
              error.response.data &&
              error.response.data.error) ||
            "Network or server error during login. Please try again.";
          this.showErrorMessage = true;
          setTimeout(() => {
            this.showErrorMessage = false;
          }, 2500);
        });
    },
  },
};
</script>

<template>
  <div class="loader-main" id="make_it_load" v-if="make_it_load">
    <div class="loader"></div>
  </div>
  <Layout>
    <div class="eauth-shell eauth-shell--wide">
      <div class="eauth-card eauth-card--split">
        <div>
          <img
            class="eauth-logo"
            src="@/assets/images/ECU/ECU-logo.jpg"
            alt="Eastern Credit Union"
          />
          <div class="eauth-accent"></div>

          <div v-if="showErrorMessage" class="eauth-alert eauth-alert--error" role="alert">
            <i class="mdi mdi-alert-circle-outline"></i>
            <span>{{ myerrors }}</span>
          </div>

          <!-- Step 1: email -->
          <form v-if="showForm1" @submit.prevent="tryToReset(true)" novalidate>
            <h1 class="eauth-title">Verify Your Email</h1>
            <p class="eauth-sub">
              Enter your email address to continue to your Eastern Credit Union
              account.
            </p>
            <div class="eauth-group">
              <label class="visually-hidden" for="useremail">Email</label>
              <div
                class="eauth-field"
                :class="{ 'is-invalid': v$.email.$error, 'eauth-field--disabled': isDisabled }"
              >
                <i class="mdi mdi-email-outline eauth-field__icon"></i>
                <input
                  type="email"
                  v-model.trim="email"
                  class="eauth-input"
                  id="useremail"
                  placeholder="Enter your email"
                  :disabled="isDisabled"
                  @blur="v$.email.$touch()"
                  autocomplete="email"
                />
              </div>
              <div
                v-for="(item, index) in v$.email.$errors"
                :key="index"
                class="eauth-error"
              >
                <span v-if="item.$message">{{ item.$message }}</span>
              </div>
            </div>
            <button
              class="eauth-btn eauth-btn--primary eauth-btn--block mt-2"
              type="submit"
              :disabled="isLoading"
            >
              <span v-if="isLoading">Sending...</span>
              <template v-else>Submit <i class="mdi mdi-arrow-right"></i></template>
            </button>
          </form>

          <!-- Step 2: password / OTP -->
          <form v-if="showForm2" @submit.prevent="tryToLogin()">
            <h1 class="eauth-title">Enter Your Password / OTP</h1>
            <p class="eauth-sub">
              Enter the password or OTP for the email below to sign in.
            </p>
            <div class="eauth-group">
              <label class="eauth-label" for="useremail">Email</label>
              <div class="eauth-field eauth-field--disabled">
                <i class="mdi mdi-email-outline eauth-field__icon"></i>
                <input
                  type="email"
                  v-model="copiedEmail"
                  class="eauth-input"
                  id="useremail"
                  placeholder="Enter email"
                  :disabled="true"
                />
              </div>
              <div class="eauth-row-end">
                <button type="button" class="eauth-link" @click="changeEmail">
                  Wrong email? Change it
                </button>
              </div>
            </div>
            <div class="eauth-group">
              <label class="eauth-label" for="otpId">Password / OTP</label>
              <div class="eauth-field">
                <i class="mdi mdi-lock-outline eauth-field__icon"></i>
                <input
                  type="text"
                  v-model="otp"
                  class="eauth-input"
                  id="otpId"
                  placeholder="Enter your password / OTP"
                  autocomplete="one-time-code"
                />
              </div>
            </div>
            <button
              class="eauth-btn eauth-btn--primary eauth-btn--block mt-2"
              type="submit"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">Submitting...</span>
              <template v-else>Submit <i class="mdi mdi-arrow-right"></i></template>
            </button>
          </form>
        </div>

        <div class="eauth-illustration-wrap" aria-hidden="true">
          <svg class="eauth-illustration" viewBox="0 0 890 910" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <!-- <radialGradient id="ecuDisc" cx="50%" cy="48%" r="52%">
                <stop offset="0" stop-color="#fff6ee" />
                <stop offset=".9" stop-color="#fdebdc" />
                <stop offset="1" stop-color="#fce3cf" />
              </radialGradient> -->
              <radialGradient id="ecuDisc" cx="50%" cy="48%" r="52%">
    <stop offset="0" stop-color="#FFFAF6" />
    <stop offset=".9" stop-color="#FFF3EA" />
    <stop offset="1" stop-color="#FDEDE1" />
</radialGradient>
              <linearGradient id="ecuBack" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color="#ffab3a" />
                <stop offset="1" stop-color="#ff8f10" />
              </linearGradient>
              <linearGradient id="ecuSide" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stop-color="#ffd682" />
                <stop offset="1" stop-color="#ffb347" />
              </linearGradient>
              <linearGradient id="ecuFlap" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stop-color="#ffe3a1" />
                <stop offset="1" stop-color="#ffc45c" />
              </linearGradient>
              <linearGradient id="ecuAt" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#ffa41f" />
                <stop offset="1" stop-color="#f26a00" />
              </linearGradient>
              <filter id="ecuShadow" x="-20%" y="-20%" width="140%" height="150%">
                <feDropShadow dx="0" dy="22" stdDeviation="22" flood-color="#ff7a1a" flood-opacity=".28" />
              </filter>
              <filter id="ecuShadowSm" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="10" stdDeviation="12" flood-color="#b45309" flood-opacity=".2" />
              </filter>
            </defs>
            <!-- <circle cx="445" cy="460" r="440" fill="#fdeee2" opacity=".55" /> -->
            <circle cx="445" cy="460" r="425" fill="url(#ecuDisc)" />
            <g filter="url(#ecuShadow)">
              <path d="M150 396 L228 332 L360 193 L430 140 Q455 120 480 140 L552 193 L668 332 L750 396 Z" fill="url(#ecuBack)" />
              <rect x="228" y="195" width="440" height="380" rx="30" fill="#fff" />
              <text x="452" y="418" text-anchor="middle" font-size="190" font-weight="700" fill="url(#ecuAt)" font-family="Arial, Helvetica, 'Segoe UI', sans-serif">@</text>
              <path d="M148 428 Q148 390 186 398 L405 535 Q450 515 495 535 L714 398 Q752 390 752 428 L752 693 Q752 733 712 733 L188 733 Q148 733 148 693 Z" fill="url(#ecuSide)" />
              <path d="M156 698 L405 538 Q450 512 495 538 L748 698 Q748 733 712 733 L188 733 Q156 733 156 698 Z" fill="url(#ecuFlap)" />
            </g>
            <circle cx="662" cy="628" r="92" fill="#fff" filter="url(#ecuShadowSm)" />
            <path d="M622 630 L652 662 L708 598" fill="none" stroke="url(#ecuAt)" stroke-width="20" stroke-linecap="round" stroke-linejoin="round" />
            <g stroke="#ff8c0a" stroke-width="20" stroke-linecap="round" fill="none">
              <path d="M630 34 L622 96" />
              <path d="M714 64 L672 117" />
              <path d="M766 129 L707 154" />
            </g>
          </svg>
        </div>
      </div>

      <div class="eauth-footer">
        <p>
          © {{ new Date().getFullYear() }} ECU. Crafted with by
          <a target="_blank" rel="noopener" href="https://midriffinfosolution.org/">MISPL</a>
        </p>
      </div>
    </div>
  </Layout>
</template>